import {
  CloudDownloadOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileUnknownOutlined,
  GifOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Empty,
  Form,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  Upload,
} from "antd";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { openNotification } from "../../../../../components/Notification";
import { anexoService } from "../../../../../services/resources/anexoService";
import { denuncianteService } from "../../../../../services/resources/denuncianteService";
import { UserContext } from "../../../../../contexts/UserContext";
import { OPCOES_TAMANHO_PAGINA, USER_ROLES, urls } from "../../../../../utils";

const CONVERT_SIZE_TO_MB = 1024 * 1024;
const MAX_SIZE_FILE_UPLOAD_MB = 30;

const MIME_TYPE_ICONS = {
  "application/pdf": <FilePdfOutlined style={{ fontSize: "1.5rem" }} />,
  "application/json": <FileTextOutlined style={{ fontSize: "1.5rem" }} />,
  "image/png": <PictureOutlined style={{ fontSize: "1.5rem" }} />,
  "image/jpg": <PictureOutlined style={{ fontSize: "1.5rem" }} />,
  "image/jpeg": <PictureOutlined style={{ fontSize: "1.5rem" }} />,
  "image/gif": <GifOutlined style={{ fontSize: "1.5rem" }} />,
  default: <FileUnknownOutlined style={{ fontSize: "1.5rem" }} />,
};

export const Anexos = ({ handleNext, onEdit }) => {
  const { user } = useContext(UserContext);
  const { codigo } = useParams();
  const [form] = Form.useForm();

  const [anexos, setAnexos] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);

  const [loadingAnexos, setLoadingAnexos] = useState(new Set());

  const transformSize = useCallback((bytes) => {
    if (bytes === 0) return "0 Bytes";
    const units = ["bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${units[i]}`;
  }, []);

  const getAtendimento = useCallback(async () => {
    setIsTableLoading(true);
    try {
      const { data } = await denuncianteService.getAtendimento(codigo);
      setAnexos(
        data?.anexos?.map((anexo) => ({
          ...anexo,
          tamanho: transformSize(anexo.tamanho),
        })) || []
      );
    } catch (error) {
      openNotification(
        "error",
        <strong>Ocorreu um erro ao buscar os dados!</strong>,
        error?.response?.data?.detail
      );
    } finally {
      setIsTableLoading(false);
    }
  }, [codigo, transformSize]);

  const handleDeleteAnexo = useCallback(
    async (codigoAnexo) => {
      setLoadingAnexos((prev) => new Set(prev).add(codigoAnexo));
      try {
        await anexoService.atendimento.delete(codigo, codigoAnexo);
        setAnexos((prev) =>
          prev.filter((anexo) => anexo.codigo !== codigoAnexo)
        );
        openNotification(
          "success",
          <strong>Anexo removido com sucesso!</strong>
        );
      } catch (error) {
        openNotification(
          "error",
          <strong>Ocorreu um erro ao remover o anexo!</strong>,
          error?.response?.data?.userMessage
        );
      } finally {
        setLoadingAnexos((prev) => {
          const next = new Set(prev);
          next.delete(codigoAnexo);
          return next;
        });
      }
    },
    [codigo]
  );

  const handleDownloadAnexo = useCallback(
    async (anexo) => {
      setLoadingAnexos((prev) => new Set(prev).add(anexo.codigo));
      try {
        const response = await anexoService.atendimento.get(
          codigo,
          anexo.codigo,
          anexo.contentType
        );
        const data = new Blob([response.data], { type: anexo.contentType });
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", anexo.nomeArquivo);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        openNotification(
          "success",
          <strong>Anexo baixado com sucesso!</strong>
        );
      } catch (error) {
        openNotification(
          "error",
          <strong>Ocorreu um erro ao baixar o anexo!</strong>,
          error?.response?.data?.detail
        );
      } finally {
        setLoadingAnexos((prev) => {
          const next = new Set(prev);
          next.delete(anexo.codigo);
          return next;
        });
      }
    },
    [codigo]
  );

  useEffect(() => {
    getAtendimento();
  }, [getAtendimento]);

  const columns = useMemo(
    () => [
      {
        key: "icone",
        width: 70,
        render: (anexo) => (
          <Space size="large" align="center">
            {MIME_TYPE_ICONS[anexo.contentType] || MIME_TYPE_ICONS.default}
          </Space>
        ),
      },
      { title: <strong>Nome</strong>, dataIndex: "nomeArquivo", key: "nome" },
      { title: <strong>Tamanho</strong>, dataIndex: "tamanho", key: "tamanho" },
      {
        title: <strong>Ações</strong>,
        key: "action",
        width: 100,
        render: (anexo) => (
          <Space size="small">
            {onEdit &&
              user?.authorities?.includes(
                USER_ROLES.DELETAR_ANEXO_DENUNCIA
              ) && (
                <Popconfirm
                  title="Deletar anexo?"
                  onConfirm={() =>
                    setTimeout(() => handleDeleteAnexo(anexo.codigo), 0)
                  }
                >
                  <Button
                    type="danger"
                    loading={loadingAnexos.has(anexo.codigo)}
                  >
                    <DeleteOutlined style={{ fontSize: "1rem" }} />
                  </Button>
                </Popconfirm>
              )}
            <Button
              onClick={() => handleDownloadAnexo(anexo)}
              loading={loadingAnexos.has(anexo.codigo)}
              title={`Baixar o arquivo ${anexo.nomeArquivo}`}
              type="dashed"
            >
              <CloudDownloadOutlined style={{ fontSize: "1.2rem" }} />
            </Button>
          </Space>
        ),
      },
    ],
    [onEdit, user, handleDeleteAnexo, handleDownloadAnexo, loadingAnexos]
  );

  const uploadProps = useMemo(
    () => ({
      name: "arquivo",
      multiple: true,
      action: `${process.env.REACT_APP_GATEWAY}${urls.ATENDIMENTO_ANEXO.replace(
        ":codigo",
        codigo
      )}`,
      method: "PUT",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("@oisol/token")}`,
      },
      showUploadList: false,
      beforeUpload: (file) => {
        const isAlreadyUploaded = anexos.some(
          (anexo) => anexo.nomeArquivo === file.name
        );
        if (isAlreadyUploaded) {
          openNotification(
            "info",
            <strong>Arquivo "{file.name}" já foi anexado.</strong>
          );
          return false;
        }
        const isTooLarge =
          file.size / CONVERT_SIZE_TO_MB > MAX_SIZE_FILE_UPLOAD_MB;
        if (isTooLarge) {
          openNotification(
            "error",
            <strong>Arquivo maior que {MAX_SIZE_FILE_UPLOAD_MB} MB.</strong>
          );
          return false;
        }
        return true;
      },
      onChange(info) {
        if (info.file.status === "done") {
          openNotification(
            "success",
            <strong>Arquivo "{info.file.name}" anexado com sucesso!</strong>
          );
          getAtendimento();
        } else if (info.file.status === "error") {
          openNotification(
            "error",
            <strong>Falha ao anexar "{info.file.name}".</strong>
          );
        }
      },
    }),
    [anexos, codigo, getAtendimento]
  );

  return (
    <>
      <Form form={form} name="Anexo" layout="vertical">
        <Row justify="end" align="middle" style={{ marginBottom: "16px" }}>
          {onEdit && (
            <Col>
              <Upload {...uploadProps}>
                <Button type="primary">Adicionar Anexo</Button>
              </Upload>
            </Col>
          )}
        </Row>
        <Row>
          <Col span={24}>
            <div style={{ minHeight: "300px" }}>
              {anexos?.length > 0 ? (
                <Table
                  loading={isTableLoading}
                  dataSource={anexos}
                  columns={columns}
                  pagination={{
                    size: "small",
                    showSizeChanger: true,
                    pageSizeOptions: OPCOES_TAMANHO_PAGINA,
                  }}
                  size="small"
                  rowKey="codigo"
                  style={{ tableLayout: "fixed" }}
                />
              ) : (
                <Empty
                  description={<h2>Sem anexos para este atendimento</h2>}
                />
              )}
            </div>
          </Col>
        </Row>
      </Form>
      <Row justify="space-between" align="bottom" style={{ marginTop: "24px" }}>
        <Tooltip
          placement="top"
          title="Todas as alterações nesta página já estão salvas!"
        >
          <Button onClick={() => handleNext("classification")}>Anterior</Button>
        </Tooltip>
        <Tooltip
          placement="top"
          title="Todas as alterações nesta página já estão salvas!"
        >
          <Button onClick={() => handleNext("associated")}>Próximo</Button>
        </Tooltip>
      </Row>
    </>
  );
};
